import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';

// @mui
import {
  Grid,
  Container,
  Stack,
  Card,
  Typography,
  Button,
  FormControlLabel,
  Box,
  Radio,
  RadioGroup,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';

// recharts
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  PieChart,
  Pie,
  ComposedChart,
  Line,
  LabelList,
} from 'recharts';

import Iconify from '../components/iconify';
import { cores } from '../styles/styleUtils';
import { GetDadosHome, getProdutos, validateUser } from './api';
import HomeVendasServicos from './HomeVendasServicos';
import config from '../config.json';
import PopoverFiltroHome from './PopoverFiltroHome';

// ----------------------------------------------------------------------
// Paleta categórica — laranja Marcki como cor-líder, com acentos harmônicos
// (teal, âmbar, azul, verde) para dar leitura a gráficos de várias categorias.
const CHART_COLORS = [
  cores.laranja, // laranja da marca
  '#2A9D8F', // teal
  '#E9A23B', // âmbar
  '#3D6E9E', // azul ardósia
  '#6BA368', // verde
  cores.laranjaEscuro, // laranja escuro (hover)
  '#C44536', // terracota
  '#8A8A8A', // cinza
];

// Rampa sequencial (claro → escuro) só para o Funil — reforça a progressão das etapas.
const RAMPA_FUNIL = ['#EDC29D', '#E3A36B', '#DA853A', cores.laranja, '#B75908', cores.laranjaEscuro, '#733805'];

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const anoAtual = new Date().getFullYear();
const anosChart = [anoAtual - 2, anoAtual - 1, anoAtual];

const fmtBRL = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => (Number(v) || 0).toLocaleString('pt-BR');
const abreviaBRL = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000000) return `R$ ${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toFixed(0)}k`;
  return `R$ ${n}`;
};

// ----------------------------------------------------------------------
// Componentes reutilizáveis

const Vazio = ({ texto = 'Sem dados para o filtro atual' }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 120 }}>
    <Typography sx={{ color: '#9e9e9e', fontSize: 14 }}>{texto}</Typography>
  </Stack>
);

const KpiCard = ({ label, value, icon }) => (
  <Card
    sx={{
      p: 2,
      height: '100%',
      textAlign: 'center',
      background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
      borderTop: `4px solid ${cores.laranja}`,
      borderRadius: 2,
      boxShadow: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
    }}
  >
    {icon && <Iconify icon={icon} sx={{ color: cores.laranja, width: 22, height: 22 }} />}
    <Typography sx={{ color: '#bdbdbd', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ color: cores.textoClaro, fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Card>
);

const ChartCard = ({ title, action, height = 320, children }) => (
  <Card sx={{ p: 2, height: '100%', borderRadius: 2, boxShadow: 3, borderTop: `4px solid ${cores.laranja}` }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, gap: 1 }}>
      <Typography sx={{ fontWeight: 700, color: cores.painelBase, fontSize: 15 }}>{title}</Typography>
      {action}
    </Stack>
    <Box sx={{ width: '100%', height }}>{children}</Box>
  </Card>
);

const rotuloPizza = ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`;

const PieMarcki = ({ data, money, start = 0 }) => {
  if (!data || data.length === 0) return <Vazio />;
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius="78%"
          label={rotuloPizza}
          labelLine={false}
        >
          {data.map((entry, i) => (
            <Cell key={`c-${i}`} fill={CHART_COLORS[(i + start) % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(v) => (money ? fmtBRL(v) : fmtNum(v))} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const BarsH = ({ data, money, palette = CHART_COLORS, start = 0 }) => {
  if (!data || data.length === 0) return <Vazio />;
  return (
    <ResponsiveContainer>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={money ? abreviaBRL : fmtNum} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 12 }} />
        <Tooltip formatter={(v) => (money ? fmtBRL(v) : fmtNum(v))} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
          {data.map((entry, i) => (
            <Cell key={`b-${i}`} fill={palette[(i + start) % palette.length]} />
          ))}
          <LabelList
            dataKey="value"
            position="right"
            formatter={money ? fmtBRL : fmtNum}
            style={{ fontSize: 11, fill: cores.painelBase }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const MetaChart = ({ realizado, meta, money }) => {
  const data = MESES.map((m, i) => ({
    mes: m,
    Realizado: Number(realizado?.[i]) || 0,
    Meta: Number(meta?.[i]) || 0,
  }));
  return (
    <ResponsiveContainer>
      <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={money ? abreviaBRL : fmtNum} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => (money ? fmtBRL(v) : fmtNum(v))} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="Realizado" fill={cores.laranja} radius={[4, 4, 0, 0]} />
        <Line type="monotone" dataKey="Meta" stroke="#2A9D8F" strokeWidth={2.5} dot={{ r: 2 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const AnoSelect = ({ value, onChange }) => (
  <Autocomplete
    disablePortal
    size="small"
    value={value}
    onChange={(e, novo) => onChange(novo || anoAtual)}
    options={anosChart}
    isOptionEqualToValue={(o, v) => o === v}
    getOptionLabel={(o) => `${o}`}
    sx={{ width: 150 }}
    renderInput={(params) => <TextField {...params} label="Ano" />}
  />
);

// ----------------------------------------------------------------------

export default function Home() {
  const navigate = useNavigate();

  // Papel do usuário
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  // eslint-disable-next-line no-unused-vars
  const isSupervisor = !!userData.supervisor;

  useEffect(() => {
    validateuser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const validateuser = async () => {
    const teste = await validateUser();
    if (!teste.ok) {
      localStorage.removeItem('userData');
      localStorage.removeItem('userToken');
      config.user = {};
      navigate('/', { replace: true });
      window.alert('Sessão expirada, faça o login novamente');
      return;
    }
    localStorage.setItem('userData', JSON.stringify(teste.usuario));
    config.user = teste.usuario;
  };

  // ---------- Filtro ----------
  const dataAtual = new Date();
  const primeiroDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
  const ultimoDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0);
  const emptyFiltro = {
    usuarios: [userData],
    datainicial: primeiroDiaDoMes,
    datafinal: ultimoDiaDoMes,
    produtos: [],
    proximociclo: true,
    anochart: anoAtual,
  };
  const [filtro, setFiltro] = useState(emptyFiltro);
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro = () => setOpenFiltro(null);
  const limparFiltro = () => {
    setFiltro({ ...emptyFiltro });
    setOpenFiltro(null);
  };
  const onConfirmFiltro = (filtroPopover) => {
    setFiltro(filtroPopover);
    setOpenFiltro(null);
  };

  // ---------- Catálogo de produtos (separar equipamento × serviço/seguro) ----------
  const [catalogo, setCatalogo] = useState([]);
  useEffect(() => {
    fetchCatalogo();
  }, []);
  const fetchCatalogo = async () => {
    try {
      const resultado = await getProdutos();
      setCatalogo(Array.isArray(resultado) ? resultado : []);
    } catch (e) {
      setCatalogo([]);
    }
  };

  // Nomes dos produtos de serviço/seguro — usados para separar por NOME (robusto a
  // ids antigos/duplicados). O painel principal exclui esses nomes; a sub-janela os inclui.
  const nomesServico = useMemo(
    () => catalogo.filter((p) => p.tipo === 'servico').map((p) => p.nome),
    [catalogo],
  );
  const nomesServSeg = useMemo(
    () => catalogo.filter((p) => p.tipo === 'servico' || p.tipo === 'seguro').map((p) => p.nome),
    [catalogo]
  );
  const temSubJanela = nomesServSeg.length > 0;

  // ---------- Dados ----------
  const [dados, setDados] = useState({});
  const [dadosServSeg, setDadosServSeg] = useState({});

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, catalogo]);

  const fetchAll = async () => {
    const selProdutos = filtro.produtos || [];
    // Painel principal: se o usuário escolheu produtos no filtro, respeita a escolha;
    // senão, mostra TUDO menos serviço/seguro (exclui por nome — pega até produtos antigos).
    const filtroMain = selProdutos.length
      ? { ...filtro, produtos: selProdutos }
      : { ...filtro, produtos: [], nomesExcluir: nomesServSeg };
    const main = await GetDadosHome(filtroMain);
    setDados(main || {});

    // Sub-janela: inclui serviço/seguro por nome, mesmo recorte de data/usuário/unidade.
    if (nomesServSeg.length) {
      const ss = await GetDadosHome({ ...filtro, produtos: [], nomesIncluir: nomesServSeg });
      setDadosServSeg(ss || {});
    } else {
      setDadosServSeg({});
    }
  };

  // ---------- Funil (precisa de fluxo selecionado) ----------
  const [tipoFunil, setTipoFunil] = useState('valor'); // 'valor' | 'quantidade' | 'kwp'
  const funilData = useMemo(() => {
    const arr = dados.propostasporetapas || [];
    return arr.map((e) => ({
      label: e.etapaNome,
      value:
        tipoFunil === 'valor'
          ? Number(e.totalValor) || 0
          : tipoFunil === 'quantidade'
            ? Number(e.quantidade) || 0
            : Number(e.totalKwp) || 0,
    }));
  }, [dados.propostasporetapas, tipoFunil]);

  // ---------- Abas ----------
  const [aba, setAba] = useState(0);

  // KPIs de venda (reutilizado nas duas abas)
  const kpisVenda = (d) => [
    { label: 'Vendas', value: fmtNum(d.numeroVendas) },
    { label: 'Total R$', value: fmtBRL(d.totalVendasValor) },
    { label: 'Ticket R$', value: fmtBRL(d.ticketMedioValor) },
    { label: 'Conversão', value: `${d.taxaConversao || 0}%` },
    { label: 'Rejeitados', value: fmtNum(d.propostasRejeitadas) },
  ];

  return (
    <>
      <Helmet>
        <title> Home </title>
      </Helmet>

      {/* Botão flutuante de filtro */}
      <Stack sx={{ zIndex: 1, position: 'fixed' }}>
        <Button
          onClick={(e) => setOpenFiltro(e.currentTarget)}
          variant="contained"
          startIcon={<Iconify icon="eva:search-fill" />}
          sx={{
            height: 50,
            minWidth: 25,
            backgroundColor: cores.laranja,
            '&:hover': { backgroundColor: cores.laranjaEscuro },
            right: 30,
            position: 'fixed',
          }}
        />
      </Stack>

      <Container maxWidth="xl" sx={{ mt: 2, pb: 6 }}>
        {temSubJanela && (
          <Tabs
            value={aba}
            onChange={(e, v) => setAba(v)}
            sx={{
              mb: 2,
              '& .MuiTabs-indicator': { backgroundColor: cores.laranja },
              '& .Mui-selected': { color: `${cores.laranjaEscuro} !important` },
            }}
          >
            <Tab label="Equipamentos" />
            <Tab label="Serviços & Seguros" />
          </Tabs>
        )}

        {/* ===================== PAINEL PRINCIPAL (EQUIPAMENTOS) ===================== */}
        {aba === 0 && (
          <Grid container spacing={2}>
            {/* Cards por fluxo / kWp */}
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Propostas" value={fmtNum(dados?.propostasFluxo?.Propostas)} icon="eva:clipboard-outline" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Vendas" value={fmtNum(dados?.propostasFluxo?.Vendas)} icon="eva:award-outline" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Engenharia" value={fmtNum(dados?.propostasFluxo?.Engenharia)} icon="eva:settings-2-outline" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Instalação" value={fmtNum(dados?.propostasFluxo?.Instalação)} icon="eva:car-outline" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Total kWp" value={(Number(dados.totalVendasKwp) || 0).toFixed(2)} icon="game-icons:solar-power" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Ticket kWp" value={(Number(dados.ticketMedioKwp) || 0).toFixed(2)} icon="eva:flash-outline" />
            </Grid>

            {/* Cards de venda */}
            {kpisVenda(dados).map((k) => (
              <Grid item xs={6} sm={4} md={2.4} key={k.label}>
                <KpiCard label={k.label} value={k.value} />
              </Grid>
            ))}

            {/* Funil + Composição */}
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Funil de Vendas"
                action={
                  <RadioGroup
                    row
                    value={tipoFunil}
                    onChange={(e) => setTipoFunil(e.target.value)}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                  >
                    <FormControlLabel value="valor" control={<Radio size="small" />} label="Valor" />
                    <FormControlLabel value="quantidade" control={<Radio size="small" />} label="Qtd" />
                    <FormControlLabel value="kwp" control={<Radio size="small" />} label="kWp" />
                  </RadioGroup>
                }
              >
                {filtro.fluxo ? (
                  <BarsH data={funilData} money={tipoFunil === 'valor'} palette={RAMPA_FUNIL} />
                ) : (
                  <Vazio texto="Selecione um fluxo no filtro para ver o funil" />
                )}
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard title="Composição de Orçamentos (por captação)">
                <PieMarcki data={dados.composicaoOrcamentos} start={1} />
              </ChartCard>
            </Grid>

            {/* Mix + Ranking */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Mix de Propostas (por produto)">
                <BarsH data={dados.propostasProdutoQuantidade} start={2} />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard title="Ranking de Vendas (por vendedor)">
                <BarsH data={dados.rankingVendedores} money start={3} />
              </ChartCard>
            </Grid>

            {/* Vendas x Meta + Vendas por Captação */}
            <Grid item xs={12} md={8}>
              <ChartCard
                title="Vendas × Meta (R$)"
                action={<AnoSelect value={filtro.anochart} onChange={(a) => setFiltro({ ...filtro, anochart: a })} />}
              >
                <MetaChart realizado={dados.propostasMes} meta={dados.metasValor} money />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartCard title="Vendas por Forma de Atendimento (R$)">
                <PieMarcki data={dados.vendasPorCaptacao} money start={4} />
              </ChartCard>
            </Grid>

            {/* Orçamentos x Meta + Vendas por Fornecedor */}
            <Grid item xs={12} md={8}>
              <ChartCard
                title="Orçamentos × Meta (qtd)"
                action={<AnoSelect value={filtro.anochart} onChange={(a) => setFiltro({ ...filtro, anochart: a })} />}
              >
                <MetaChart realizado={dados.propostaMesQuantidade} meta={dados.metasQuantidade} />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartCard title="Vendas por Fornecedor (R$)">
                <PieMarcki data={dados.vendasPorFornecedor} money start={6} />
              </ChartCard>
            </Grid>

            {/* Top Cidades + Estados */}
            <Grid item xs={12} md={8}>
              <ChartCard title="Top 10 Cidades (R$)">
                <BarsH data={dados.top10cidades} money />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartCard title="Vendas por Estado (R$)">
                <PieMarcki data={dados.propostasEstados} money start={1} />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {/* ===================== SUB-JANELA: SERVIÇOS & SEGUROS ===================== */}
        {temSubJanela && aba === 1 && (
          <Grid container spacing={2}>
            {kpisVenda(dadosServSeg).map((k) => (
              <Grid item xs={6} sm={4} md={2.4} key={`ss-${k.label}`}>
                <KpiCard label={k.label} value={k.value} />
              </Grid>
            ))}

            <Grid item xs={12} md={6}>
              <ChartCard title="Mix (Serviços × Seguros)">
                <BarsH data={dadosServSeg.propostasProdutoQuantidade} start={2} />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard title="Ranking de Vendas (por vendedor)">
                <BarsH data={dadosServSeg.rankingVendedores} money start={3} />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={8}>
              <ChartCard title="Vendas por mês (R$)">
                <MetaChart realizado={dadosServSeg.propostasMes} meta={dadosServSeg.metasValor} money />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartCard title="Vendas por Forma de Atendimento (R$)">
                <PieMarcki data={dadosServSeg.vendasPorCaptacao} money start={4} />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={8}>
              <ChartCard title="Top 10 Cidades (R$)">
                <BarsH data={dadosServSeg.top10cidades} money />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartCard title="Composição de Orçamentos (por captação)">
                <PieMarcki data={dadosServSeg.composicaoOrcamentos} start={1} />
              </ChartCard>
            </Grid>

            <Grid item xs={12}>
              <HomeVendasServicos nomesServico={nomesServico} />
            </Grid>
          </Grid>
        )}
      </Container>

      {openFiltro ? (
        <PopoverFiltroHome
          filtro={filtro}
          open={openFiltro}
          onClose={onCloseFiltro}
          onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro}
        />
      ) : null}
    </>
  );
}